import React from "react";
import { Grid, IconButton, Tooltip } from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  useGetList,
  useSimpleFormIterator,
} from "react-admin";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useWatch } from "react-hook-form";
import { settings } from "app";
import { InternshipSessionInput } from "server/components/Fields/InternshipSessionInput";
import AcademicYearInput from "components/LearningActivity/CreateWeeklySchedule/components/Inputs/AcademicYearInput";
import { administrationRequests } from "api/administrationApi";
import WardEmployeeRow from "./Partials/WardEmployeeRow";

// „Добави“ is disabled once a new row would have no ward left to pick: the wards the backend still
// returns (excluding the ones already chosen - the same request a row's select makes) are all
// "held" by rows that have no ward yet. Stays enabled while that list is loading.
const useCanAddWardRow = () => {
  const wardsEmployees = useWatch({ name: "wardsEmployees" });
  const chosenWardIds = wardsEmployees?.map(row => row?.wardId).filter(Boolean) ?? [];
  const rowsWithoutWard = (wardsEmployees?.length ?? 0) - chosenWardIds.length;

  const { data: remainingWards, isPending } = useGetList(administrationRequests.getWardsSelect(), {
    filter: chosenWardIds.length ? { excludeIds: chosenWardIds } : {},
  });

  return isPending || (remainingWards?.length ?? 0) > rowsWithoutWard;
};

// The iterator's add button, shown disabled (not hidden, as `disableAdd` would) when no ward is left.
// The <span> lets the tooltip work on a disabled button, which MUI otherwise warns about.
const AddWardRowButton = ({ disabled }) => {
  const { add } = useSimpleFormIterator();
  return (
    <Tooltip title={disabled ? "Няма повече болнични отделения за добавяне" : "Добави"}>
      <span>
        <IconButton
          size="small"
          color="primary"
          onClick={() => add()}
          disabled={disabled}
        >
          <AddCircleOutlineIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const CreateForm = ({ filters }) => {
  const formStructuralUnitId = useWatch({ name: "structuralUnitId" });
  const structuralUnitId = formStructuralUnitId ?? filters?.structuralUnitId;
  const canAddWardRow = useCanAddWardRow();

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={6}
        >
          <AcademicYearInput withoutLanguage />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <ReferenceInput
            source="structuralUnitId"
            reference={administrationRequests.getStructuralUnitsSelect()}
            filter={{ filterByUser: true }}
          >
            <SelectInput
              optionText="name"
              label="Структурна единица"
              emptyText={false}
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
        {structuralUnitId && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              <InternshipSessionInput
                key={structuralUnitId}
                filter={{ structuralUnitId }}
              />
            </Grid>
          </>
        )}
        <Grid
          item
          xs={12}
          md={6}
        >
          <ReferenceInput
            source="buildingId"
            reference={administrationRequests.getBuildingsSelect()}
            label="Сграда"
          >
            <AutocompleteInput
              label="Сграда"
              optionText="name"
              optionValue="id"
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <ArrayInput
            source="wardsEmployees"
            label="Отделения служители"
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              disableClear
              addButton={<AddWardRowButton disabled={!canAddWardRow} />}
              inline
              sx={settings.style.iteratorWithoutGap}
            >
              <WardEmployeeRow />
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
    </>
  );
};

export default CreateForm;
